import { motion, AnimatePresence } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import OptimizedPremiumProductCard from '@/components/Products/OptimizedPremiumProductCard';
import { Product } from '@/types/common';
import { ShoppingBag, Sparkles } from 'lucide-react';

interface CategoryGridProps {
  products?: Product[];
  isLoading: boolean;
  viewDensity: 'comfortable' | 'compact' | 'dense';
  isMobile: boolean;
}

const CategoryGrid = ({ products, isLoading, viewDensity, isMobile }: CategoryGridProps) => {
  // Grid configuration based on density and device
  const getGridConfig = () => {
    if (isMobile) {
      return 'grid-cols-2 gap-4';
    }
    
    switch (viewDensity) {
      case 'comfortable':
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8';
      case 'compact':
        return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6';
      case 'dense':
        return 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4';
      default:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8';
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={`grid ${getGridConfig()}`}>
        {Array.from({ length: isMobile ? 6 : 12 }).map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            className="space-y-4"
          >
            <Skeleton className="aspect-[4/3] w-full rounded-modern" variant="shimmer" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-6 w-1/4" />
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!products || products.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col items-center justify-center py-20 text-center"
      >
        <motion.div
          className="relative mb-8"
          animate={{ 
            rotate: [0, 5, -5, 0],
            scale: [1, 1.05, 1]
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        >
          <div className="bg-gradient-muted p-8 rounded-full">
            <ShoppingBag className="h-16 w-16 text-muted-foreground" />
          </div>
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity,
              ease: "easeInOut" 
            }}
          >
            <Sparkles className="h-6 w-6 text-primary" />
          </motion.div>
        </motion.div>
        
        <h3 className="text-2xl font-playfair font-semibold text-foreground mb-4">
          No Products Found
        </h3>
        <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
          We couldn't find any products matching your criteria. 
          Try adjusting your filters or browse our featured collections.
        </p>
      </motion.div>
    );
  }

  // Products grid
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={`${viewDensity}-${products.length}`}
          layout
          className={`grid ${getGridConfig()}`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ 
            duration: 0.3,
            staggerChildren: 0.05 
          }}
        >
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: { delay: index * 0.05 }
              }}
              exit={{ opacity: 0, scale: 0.9 }}
              whileHover={{ y: -5 }}
              transition={{ 
                duration: 0.3,
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
            >
              <OptimizedPremiumProductCard 
                product={product}
              />
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

export default CategoryGrid;